import { z } from 'zod';
import { isoDateTime, jsonObject, riskLevel, uuid } from './primitives';
import { autonomyPosture, DEFAULT_AUTONOMY_POSTURE } from './episode';
import { toolResult } from './tool';

/**
 * Quick-chat assistant wire contracts (§21, v1.7, WP-C1). A built-in,
 * per-workspace assistant (D5): each user message runs ONE episode on the same
 * AgentRuntime + ToolGateway an agent uses (§8/§9), synchronous and streaming
 * over SSE (D3). Persisted in a DEDICATED lightweight model — `chat_sessions` +
 * `chat_messages` (D4) — NOT `agent_runs`. `autonomyPosture` is the task-agent
 * posture (§13.3) REUSED VERBATIM, enforced at the gateway exactly as for a task
 * agent: a read turn streams back instantly, an action turn pauses inline for
 * approval.
 *
 * These are CONTRACTS ONLY (WP-C1) — the streaming ChatService (WP-C2), the SSE
 * endpoint (WP-C3), and the web panel (WP-C4) consume this vocabulary; no work
 * package invents a parallel shape (§8.2).
 */

/** A chat turn's author. Mirrors `chat_messages.role` (TEXT + CHECK). */
export const chatRole = z.enum(['user', 'assistant']);
export type ChatRole = z.infer<typeof chatRole>;

/**
 * A chat conversation (`chat_sessions`). `runtimeSessionRef` is the opaque
 * Claude SDK session handle (mirrors `agent_runs.runtime_session_ref`, D5) that
 * carries conversation continuity across turns — null until the first turn
 * completes. `autonomyPosture` defaults to the safest `approval_only`, same as a
 * task agent (§13.3) — an owner must consciously loosen it.
 */
export const chatSession = z.object({
  id: uuid,
  workspaceId: uuid,
  runtimeSessionRef: z.string().nullable(),
  autonomyPosture,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type ChatSession = z.infer<typeof chatSession>;

/** One persisted turn (`chat_messages`) — the user's message or the assistant's
 * final response text. Streamed deltas/tool-call chips are NOT separate rows
 * here; see `chatStreamEvent` below for the live wire vocabulary. */
export const chatMessage = z.object({
  id: uuid,
  chatSessionId: uuid,
  role: chatRole,
  content: z.string(),
  createdAt: isoDateTime,
});
export type ChatMessage = z.infer<typeof chatMessage>;

/**
 * POST …/chat/messages request (WP-C3). `sessionId` is omitted to start a NEW
 * chat session (the server creates one, defaulting `autonomyPosture` to
 * `DEFAULT_AUTONOMY_POSTURE`); present to continue an existing conversation.
 */
export const sendChatMessageRequest = z.object({
  content: z.string().trim().min(1),
  sessionId: uuid.optional(),
});
export type SendChatMessageRequest = z.infer<typeof sendChatMessageRequest>;

// ── Streamed SSE event vocabulary (§21, "synchronous and streaming over SSE",
//    D3). Unlike the Operations stream (`sse.ts`'s `streamEvent`, which carries
//    only small routing hints for a client to refetch canonical state), a chat
//    turn streams actual CONTENT — model text deltas and tool-call progress —
//    because the chat panel IS the live view of the turn, not a projection of
//    it. WP-C3 emits these; WP-C2's ChatService produces them from the episode
//    runner's streaming hooks. Kept minimal but complete per §21's four turn
//    phases: the model talks, it may call a tool (which may need approval), and
//    the turn ends. ──

/**
 * The model's extended-reasoning summary for the current turn (§21, v1.11). Live-only
 * (like tool-call events, NOT a persisted `chat_messages` row) — the panel folds it
 * into a collapsible "Thinking" section on the turn's assistant bubble, the way Claude
 * shows "Thought for a moment ▸". Emitted BEFORE that turn's `model_delta` when the SDK
 * returns a summarized-reasoning block (chat requests `thinking.display:'summarized'`).
 */
export const chatThinkingEvent = z.object({
  kind: z.literal('thinking'),
  sessionId: uuid,
  text: z.string(),
});
export type ChatThinkingEvent = z.infer<typeof chatThinkingEvent>;

/** The model produced another chunk of assistant text (streamed as it's generated). */
export const chatModelDeltaEvent = z.object({
  kind: z.literal('model_delta'),
  sessionId: uuid,
  text: z.string(),
});
export type ChatModelDeltaEvent = z.infer<typeof chatModelDeltaEvent>;

/** A tool call was issued and is executing through the gateway (I2). */
export const chatToolCallStartedEvent = z.object({
  kind: z.literal('tool_call_started'),
  sessionId: uuid,
  tool: z.string(),
  arguments: jsonObject,
});
export type ChatToolCallStartedEvent = z.infer<typeof chatToolCallStartedEvent>;

/** A previously-started tool call finished (ok or error, §8.2 ToolResult). */
export const chatToolCallFinishedEvent = z.object({
  kind: z.literal('tool_call_finished'),
  sessionId: uuid,
  tool: z.string(),
  result: toolResult,
});
export type ChatToolCallFinishedEvent = z.infer<typeof chatToolCallFinishedEvent>;

/**
 * An action turn paused inline for approval (§10, §21 "pauses inline for
 * approval"). The chat panel renders Approve/Reject; on approve the platform
 * executes with the exact stored arguments and the turn continues (D4
 * platform-executed), on reject the model is told and adapts.
 */
export const chatApprovalRequiredEvent = z.object({
  kind: z.literal('approval_required'),
  sessionId: uuid,
  approvalId: uuid,
  tool: z.string(),
  reason: z.string(),
  risk: riskLevel,
});
export type ChatApprovalRequiredEvent = z.infer<typeof chatApprovalRequiredEvent>;

/**
 * The turn ended. `status` mirrors the episode's terminal outcome (§8.2
 * EpisodeResult), collapsed to what the chat panel needs to render: a normal
 * completion carries the persisted assistant `chat_messages` row id; a failure
 * or limit carries no message (nothing was persisted) plus a short `error` the
 * panel can show inline. One event kind for "the turn is over", not a parallel
 * error channel.
 */
export const chatTurnDoneEvent = z.object({
  kind: z.literal('turn_done'),
  sessionId: uuid,
  status: z.enum(['completed', 'failed', 'limit_reached']),
  messageId: uuid.optional(),
  error: z.string().optional(),
});
export type ChatTurnDoneEvent = z.infer<typeof chatTurnDoneEvent>;

export const chatStreamEvent = z.discriminatedUnion('kind', [
  chatThinkingEvent,
  chatModelDeltaEvent,
  chatToolCallStartedEvent,
  chatToolCallFinishedEvent,
  chatApprovalRequiredEvent,
  chatTurnDoneEvent,
]);
export type ChatStreamEvent = z.infer<typeof chatStreamEvent>;

/** The default autonomy posture for a NEW chat session (§13.3/§21) — the safest;
 * an owner must consciously loosen it. Re-exported here for chat call sites so
 * they need not import from `episode.ts` for this one constant. */
export { DEFAULT_AUTONOMY_POSTURE as DEFAULT_CHAT_AUTONOMY_POSTURE };
